import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import GradientButton from '../ui/GradientButton';

const navItems = [
  { label: 'Home', href: '#home', color: '#00D4FF' },
  { label: 'Services', href: '#services', color: '#0099FF' },
  { label: 'About', href: '#about', color: '#00D4FF' },
  { label: 'Tech Stack', href: '#tech-stack', color: '#0066FF' },
  { label: 'Contact', href: '#contact', color: '#0099FF' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-[rgba(5,5,16,0.85)] backdrop-blur-2xl border-b border-[rgba(255,255,255,0.06)]' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <nav className="flex items-center justify-between h-16 md:h-20">
            <button onClick={() => scrollTo('#home')} className="flex items-center gap-2" aria-label="Home">
              <img src="/abci-logo.png" alt="ABCI" className="h-10 sm:h-14 md:h-44 object-contain" />
            </button>
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item, i) => (
                <motion.button
                  key={item.href}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                  onClick={() => scrollTo(item.href)}
                  whileHover={{ scale: 1.05, color: item.color }}
                  className="px-4 py-2 text-[#8A8AA3] hover:text-[#F0F0F5] text-sm font-medium transition-colors rounded-lg hover:bg-[rgba(255,255,255,0.04)]"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.label}
                </motion.button>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="ml-4">
                <GradientButton onClick={() => scrollTo('#contact')} className="!px-5 !py-2.5 !text-sm !rounded-full">
                  Start Your Project
                </GradientButton>
              </motion.div>
            </div>
            <button className="md:hidden text-[#F0F0F5] p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-[#050510]/95 backdrop-blur-xl" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
              className="absolute top-16 left-0 right-0 bg-[#0A0A1B]/95 backdrop-blur-xl border-b border-[rgba(255,255,255,0.06)] p-6"
            >
              {navItems.map((item, i) => (
                <motion.button
                  key={item.href}
                  initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}
                  onClick={() => scrollTo(item.href)}
                  className="block w-full text-left text-[#8A8AA3] hover:text-white text-lg font-medium py-3 border-b border-[rgba(255,255,255,0.04)] transition-colors"
                >
                  {item.label}
                </motion.button>
              ))}
              <GradientButton onClick={() => scrollTo('#contact')} className="w-full mt-4">
                Start Your Project
              </GradientButton>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
